import React from 'react';
import { motion } from 'framer-motion';

const CyberBackground = () => {
  // Generate random positions for nodes
  const nodes = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 3 + Math.random() * 2,
  }));

  // Generate connection lines between some nodes
  const connections = [
    { x1: 10, y1: 20, x2: 30, y2: 40 },
    { x1: 70, y1: 15, x2: 90, y2: 35 },
    { x1: 20, y1: 60, x2: 45, y2: 80 },
    { x1: 60, y1: 70, x2: 85, y2: 55 },
    { x1: 15, y1: 85, x2: 40, y2: 65 },
    { x1: 50, y1: 20, x2: 75, y2: 45 },
    { x1: 80, y1: 80, x2: 95, y2: 60 },
    { x1: 5, y1: 45, x2: 25, y2: 25 },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a1f2e] via-[#0f2d42] to-[#1a3a52]" />
      
      {/* Animated grid pattern */}
      <div className="absolute inset-0 opacity-20">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="cyber-grid" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#10b981" strokeWidth="0.5" opacity="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#cyber-grid)" />
        </svg>
      </div>

      {/* Hexagon pattern overlay */}
      <div className="absolute inset-0 opacity-10">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hexagons" width="50" height="43.4" patternUnits="userSpaceOnUse" patternTransform="scale(2)">
              <polygon points="24.8,22 37.3,29.2 37.3,43.7 24.8,50.9 12.3,43.7 12.3,29.2" fill="none" stroke="#10b981" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hexagons)" />
        </svg>
      </div>

      {/* Animated connection lines */}
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0" />
            <stop offset="50%" stopColor="#10b981" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </linearGradient>
        </defs>
        
        {connections.map((conn, i) => (
          <motion.line
            key={i}
            x1={`${conn.x1}%`}
            y1={`${conn.y1}%`}
            x2={`${conn.x2}%`}
            y2={`${conn.y2}%`}
            stroke="url(#line-gradient)"
            strokeWidth="1"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ 
              pathLength: [0, 1, 0],
              opacity: [0, 0.6, 0]
            }}
            transition={{
              duration: 4 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.8,
              ease: "easeInOut"
            }}
          />
        ))}
      </svg>

      {/* Floating nodes/particles */}
      {nodes.map((node) => (
        <motion.div
          key={node.id}
          className="absolute"
          style={{
            left: `${node.x}%`,
            top: `${node.y}%`,
          }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{
            opacity: [0.2, 0.6, 0.2],
            scale: [0.8, 1.2, 0.8],
          }}
          transition={{
            duration: node.duration,
            repeat: Infinity,
            delay: node.delay,
            ease: "easeInOut"
          }}
        >
          <div className="w-2 h-2 bg-[#10b981] rounded-full shadow-lg shadow-[#10b981]/50" />
        </motion.div>
      ))}

      {/* Larger glowing orbs */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#10b981]/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
      />
      <motion.div
        className="absolute top-1/2 right-1/3 w-48 h-48 bg-cyan-500/10 rounded-full blur-2xl"
        animate={{
          scale: [1, 1.4, 1],
          opacity: [0.2, 0.5, 0.2],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
      />

      {/* Circuit-like decorative elements */}
      <svg className="absolute bottom-0 left-0 w-full h-32 opacity-20" viewBox="0 0 1200 100" preserveAspectRatio="none">
        <motion.path
          d="M0,50 L100,50 L120,30 L200,30 L220,50 L400,50 L420,70 L500,70 L520,50 L700,50 L720,30 L800,30 L820,50 L1000,50 L1020,70 L1100,70 L1120,50 L1200,50"
          fill="none"
          stroke="#10b981"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 3, ease: "easeInOut" }}
        />
      </svg>

      {/* Top circuit decoration */}
      <svg className="absolute top-20 right-0 w-1/3 h-24 opacity-15" viewBox="0 0 400 100">
        <motion.path
          d="M400,50 L350,50 L330,30 L280,30 L260,50 L200,50 L180,70 L130,70 L110,50 L50,50 L30,30 L0,30"
          fill="none"
          stroke="#10b981"
          strokeWidth="1.5"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2.5, ease: "easeInOut", delay: 0.5 }}
        />
        <circle cx="350" cy="50" r="4" fill="#10b981" opacity="0.8" />
        <circle cx="200" cy="50" r="4" fill="#10b981" opacity="0.8" />
        <circle cx="50" cy="50" r="4" fill="#10b981" opacity="0.8" />
      </svg>

      {/* Shield/lock icon floating */}
      <motion.div
        className="absolute top-1/3 left-[10%] opacity-10"
        animate={{
          y: [-10, 10, -10],
          rotate: [-5, 5, -5],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="1">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      </motion.div>

      {/* Data flow lines on the right */}
      <motion.div
        className="absolute top-1/2 right-[5%] opacity-15"
        animate={{
          y: [-20, 20, -20],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <svg width="60" height="120" viewBox="0 0 60 120">
          <line x1="30" y1="0" x2="30" y2="30" stroke="#10b981" strokeWidth="2" />
          <circle cx="30" cy="35" r="5" fill="none" stroke="#10b981" strokeWidth="1.5" />
          <line x1="30" y1="40" x2="30" y2="70" stroke="#10b981" strokeWidth="2" />
          <rect x="20" y="70" width="20" height="15" rx="2" fill="none" stroke="#10b981" strokeWidth="1.5" />
          <line x1="30" y1="85" x2="30" y2="120" stroke="#10b981" strokeWidth="2" />
        </svg>
      </motion.div>

      {/* Binary/code rain effect on left side */}
      <div className="absolute left-4 top-32 bottom-32 w-px overflow-hidden opacity-20">
        <motion.div
          className="flex flex-col gap-4 text-[#10b981] text-xs font-mono"
          animate={{ y: [0, -200] }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
        >
          {Array.from({ length: 20 }).map((_, i) => (
            <span key={i}>{Math.random() > 0.5 ? '1' : '0'}</span>
          ))}
        </motion.div>
      </div>

      {/* Vignette overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f2e]/80 via-transparent to-[#0a1f2e]/40" />
    </div>
  );
};

export default CyberBackground;
